import { useState } from 'react'
import { Modal } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { Refresh01 } from 'blaze-ui/icons/20/Refresh01'
import { isVideo } from 'components/ImagePickerModal/utils'
import Styles from '../RegenerateDesignModal.module.scss'

interface RefineSettingsProps {
  topic: string
  setTopic: (topic: string) => void
  sourceMediaUrl: string
  error: string | null
  generationCost: number
  isLoading: boolean
  onClose: () => void
  onRegenerate: () => void
  onReplaceImage: () => void
  provider?: string
  hasInsufficientCredits?: boolean
}

const REGENERATE_LABELS: Record<string, string> = {
  veo3: 'AI Video',
  ai_generated: 'Stylized Image',
}

const getRegenerateButtonText = (provider?: string, isLoading?: boolean, hasInsufficientCredits?: boolean): string => {
  if (hasInsufficientCredits) {
    return 'Add credits'
  }
  const label = (provider && REGENERATE_LABELS[provider]) || 'Post'
  return isLoading ? `Regenerating ${label}...` : `Regenerate ${label}`
}

type AspectRatio = 'landscape' | 'portrait' | 'square'

const getAspectRatio = (width: number, height: number): AspectRatio => {
  if (width > height * 1.2) return 'landscape'
  if (height > width * 1.2) return 'portrait'
  return 'square'
}

interface SourceMediaPreviewProps {
  url: string
  className?: string
  onRatioDetected?: (ratio: AspectRatio) => void
}

const SourceMediaPreview = ({ url, className, onRatioDetected }: SourceMediaPreviewProps) => {
  if (isVideo({ url })) {
    return (
      <video
        src={url}
        className={className}
        autoPlay
        loop
        muted
        playsInline
        onLoadedMetadata={(e) => {
          const video = e.currentTarget
          onRatioDetected?.(getAspectRatio(video.videoWidth, video.videoHeight))
        }}
      />
    )
  }

  return (
    <img
      src={url}
      alt="Source"
      className={className}
      onLoad={(e) => {
        const img = e.currentTarget
        onRatioDetected?.(getAspectRatio(img.naturalWidth, img.naturalHeight))
      }}
    />
  )
}

export const RefineSettings = ({
  topic,
  setTopic,
  sourceMediaUrl,
  error,
  generationCost,
  isLoading,
  onClose,
  onRegenerate,
  onReplaceImage,
  provider,
  hasInsufficientCredits,
}: RefineSettingsProps) => {
  const isDisabled = isLoading
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('landscape')

  const thumbnailContainerClass = [Styles.thumbnailContainer, Styles[`thumbnailContainer--${aspectRatio}`]].join(' ')

  if (isPreviewOpen) {
    return (
      <>
        <Modal.Header title="Source Image" onClose={() => setIsPreviewOpen(false)} />
        <Modal.Content>
          <div className={`${Styles.thumbnailPreview} ${Styles[`thumbnailPreview--${aspectRatio}`]}`}>
            <SourceMediaPreview url={sourceMediaUrl} className={Styles.thumbnail} />
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Button variant="tertiary" size="lg" onPress={() => setIsPreviewOpen(false)}>
              Back
            </Button>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <Button size="lg" frontIcon={Refresh01} onPress={onReplaceImage}>
              Replace
            </Button>
          </Modal.FooterContent>
        </Modal.Footer>
      </>
    )
  }

  return (
    <>
      <Modal.Header title="Regenerate post" onClose={onClose} />
      <Modal.Content>
        <div className={Styles.reviewContent}>
          <div className={Styles.formSection}>
            <div className={Styles.topicLabel}>
              <Text variant="primary" color="dark-90">
                Revise topic
              </Text>
            </div>
            <textarea
              id="refine-topic"
              className={Styles.textarea}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Enter the topic for refinement..."
              disabled={isDisabled}
            />
          </div>
          <div className={Styles.formSection}>
            <div className={Styles.sourceHeader}>
              <Text variant="primary" color="dark-90">
                Change source media
              </Text>
              <Button variant="tertiary" size="sm" frontIcon={Refresh01} onPress={onReplaceImage}>
                Replace
              </Button>
            </div>
            <button className={thumbnailContainerClass} onClick={() => setIsPreviewOpen(true)}>
              <SourceMediaPreview url={sourceMediaUrl} className={Styles.thumbnail} onRatioDetected={setAspectRatio} />
            </button>
          </div>
        </div>
        {error && <div className={Styles.error}>{error}</div>}
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button variant="tertiary" size="lg" onPress={onClose} isDisabled={isDisabled}>
            Close
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button size="lg" onPress={onRegenerate} isDisabled={!topic.trim() || isDisabled}>
            <div className={Styles.generationCostContainer}>
              {getRegenerateButtonText(provider, isLoading, hasInsufficientCredits)}
              {!isLoading && generationCost > 0 && (
                <div className={Styles.generationCost}>
                  <Credits /> {generationCost}
                </div>
              )}
            </div>
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
